import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  Radio,
  RadioGroup,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { surveyAPI } from '../services/api';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Survey, Answer } from '../types';
import '../styles/survey.css';

const TakeSurvey: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState<Survey | null>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [startTime] = useState(Date.now());
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({});
  const [showIntro, setShowIntro] = useState<boolean>(false);
  const [currentIdx, setCurrentIdx] = useState<number>(0); // one-question-per-page index

  useEffect(() => {
    fetchSurvey();
  }, [id]);

  const fetchSurvey = async () => {
    try {
      const response = await surveyAPI.getSurvey(id!);
      const surveyData = response.data;
      
      if (surveyData.hasResponded) {
        setError('You have already completed this survey');
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      }

      setSurvey(surveyData);
      setShowIntro(!!(surveyData.introHtml && String(surveyData.introHtml).trim().length > 0));

      // Initialize answers (skip display-only questions)
      const initialAnswers: Answer[] = surveyData.questions
        .filter((q: any) => q.questionType !== 'display')
        .map((q: any) => {
          if (q.questionType === 'rating') {
            // Do NOT prefill rating; start as null so user explicitly selects
            return { questionId: q._id!, answer: null } as unknown as Answer;
          }
          // Other types start empty string (unanswered)
          return { questionId: q._id!, answer: '' } as Answer;
        });
      setAnswers(initialAnswers);

      // Initialize comment drafts for questions that allow comments
      const drafts: Record<string, string> = {};
      (surveyData.questions as any[]).forEach((q: any) => {
        if (shouldAllowComment(q)) {
          drafts[q._id!] = '';
        }
      });
      setCommentDrafts(drafts);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch survey');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setError('');
    if (currentIdx > 0) {
      setCurrentIdx((i) => i - 1);
    } else {
      // At first question: go back to intro
      setShowIntro(true);
    }
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers((prev) =>
      prev.map((a) => (a.questionId === questionId ? { ...a, answer: value } : a))
    );
  };

  const goNext = () => {
    setError('');
    const nonDisplay = (survey?.questions as any[]).filter((q) => q.questionType !== 'display') || [];
    const q = nonDisplay[currentIdx];
    if (!q) return;
    if (q.required) {
      const a = answers.find((x) => x.questionId === q._id);
      const val: any = a?.answer;
      const empty =
        a === undefined ||
        val === '' ||
        val === null ||
        val === undefined ||
        (Array.isArray(val) && val.length === 0);
      if (empty) {
        setError('Please answer this required question');
        return;
      }
    }
    if (currentIdx < nonDisplay.length - 1) {
      setCurrentIdx((i) => i + 1);
    }
  };

  const shouldAllowComment = (question: any) => {
    // Default enabled for rating & multiple_choice, or explicit allowComment
    if (typeof question.allowComment === 'boolean') return !!question.allowComment;
    return question.questionType === 'rating' || question.questionType === 'multiple_choice';
  };

  const commitComment = (questionId: string) => {
    const draft = commentDrafts[questionId] ?? '';
    setAnswers((prev) => prev.map((a) => (a.questionId === questionId ? { ...a, comment: draft } : a)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate required questions
    const requiredQuestions = survey?.questions.filter((q) => q.required) || [];
    const missingAnswers = requiredQuestions.filter((q) => {
      const answer = answers.find((a) => a.questionId === q._id);
      const val: any = answer?.answer;
      const empty =
        !answer ||
        val === '' ||
        val === null ||
        val === undefined ||
        (Array.isArray(val) && val.length === 0);
      return empty;
    });

    if (missingAnswers.length > 0) {
      setError('Please answer all required questions');
      return;
    }

    setSubmitting(true);

    // Ensure all comment drafts are committed before submit
    const mergedAnswers: Answer[] = answers.map((a) => {
      const draft = commentDrafts[a.questionId];
      if (typeof draft === 'string') {
        return { ...a, comment: draft } as Answer;
      }
      return a;
    });

    try {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      await surveyAPI.submitResponse(id!, { answers: mergedAnswers, timeSpent });

      navigate(`/surveys/${id}/complete`, {
        state: { message: 'Thanks for completing the survey.' },
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!survey) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Survey not found</Alert>
      </Container>
    );
  }

  const nonDisplayQuestions = (survey.questions as any[]).filter((q) => q.questionType !== 'display');
  const progress = (
    answers.filter((a) => {
      const val: any = a.answer;
      const answered =
        val !== '' &&
        val !== null &&
        val !== undefined &&
        (!Array.isArray(val) || val.length > 0);
      return answered;
    }).length /
    Math.max(1, nonDisplayQuestions.length)
  ) * 100;
  const currentQuestion = nonDisplayQuestions[currentIdx];
  const isLast = currentIdx >= Math.max(0, nonDisplayQuestions.length - 1);

  const formId = 'survey-form';

  return (
    <Container
      className="survey-page"
      maxWidth="md"
      sx={{
        mt: 0,
        mb: 0,
        py: 4,
        backgroundColor: '#fafafa',
        minHeight: '100vh',
        pb: 12, // space for fixed footer
      }}
    >
      <Paper className="survey-container" elevation={0} sx={{ p: 0, backgroundColor: 'transparent', boxShadow: 'none' }}>
        {/* Header (show title only on intro) */}
        {showIntro && (
          <Typography className="survey-title" variant="h4" component="h1" gutterBottom>
            {survey.title}
          </Typography>
        )}
        {survey.description && !showIntro && (
          <Typography className="survey-description" variant="body1" color="text.secondary" paragraph>
            {survey.description}
          </Typography>
        )}

        {/* Intro screen */}
        {showIntro ? (
          <Box>
            {/* Intro content without subtitle, borders, or padding */}
            <Box className="survey-intro" sx={{ mb: 3 }}>
              <Box dangerouslySetInnerHTML={{ __html: survey.introHtml || '' }} />
            </Box>
          </Box>
        ) : (
          <>
            {/* Top bar on question pages */}
            <Box className="survey-topbar" sx={{ mb: 2 }}>
              <Box className="survey-topbarRow" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <IconButton aria-label="Back" onClick={goBack} size="small" className="survey-topbarBack">
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography className="survey-topbarTitle" variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {survey.title}
                </Typography>
                <IconButton aria-label="Close" onClick={() => navigate('/dashboard')} size="small" className="survey-topbarClose">
                  <CloseIcon />
                </IconButton>
              </Box>
              <LinearProgress className="survey-progressBar" variant="determinate" value={progress} sx={{ mt: 1 }} />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" className="survey-form" id={formId} onSubmit={handleSubmit}>
              {currentQuestion ? (
                <Box
                  key={currentQuestion._id}
                  className={`survey-question survey-question--${currentQuestion.questionType}${currentQuestion.required ? ' survey-question--required' : ''}`}
                  sx={{ mb: 4 }}
                >
                  <FormControl className="survey-questionControl" fullWidth>
                    <FormLabel className="survey-questionLabel">
                      <Typography className="questiontext" variant="h6" gutterBottom>
                        {currentQuestion.questionText}
                        {currentQuestion.required && (
                          <Typography component="span" color="error"> *</Typography>
                        )}
                      </Typography>
                    </FormLabel>

                    {currentQuestion.questionType === 'text' && (
                      <TextField
                        className="survey-input survey-input--text"
                        fullWidth
                        multiline
                        rows={3}
                        value={answers.find((a) => a.questionId === currentQuestion._id)?.answer || ''}
                        onChange={(e) => handleAnswerChange(currentQuestion._id!, e.target.value)}
                        placeholder="Enter your answer..."
                        required={currentQuestion.required}
                      />
                    )}

                    {currentQuestion.questionType === 'rating' && (
                      <Box className="survey-rating" sx={{ mt: 1 }}>
                        <Box className="survey-ratingList">
                          {(() => {
                            const scale = Number(currentQuestion.ratingScale) || 5;
                            const defaultLikert5 = ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];
                            const labels: string[] = (Array.isArray(currentQuestion.ratingLabels) && currentQuestion.ratingLabels.length > 0)
                              ? currentQuestion.ratingLabels
                              : (scale === 5 ? defaultLikert5 : Array.from({ length: scale }, (_, i) => `${i + 1}`));
                            return Array.from({ length: scale }).map((_, idx) => {
                              const label = labels[idx] ?? `${idx + 1}`;
                              const selected = (answers.find((x) => x.questionId === currentQuestion._id)?.answer === idx + 1);
                              return (
                                <Box
                                  key={idx}
                                  role="button"
                                  tabIndex={0}
                                  className={`survey-ratingOption${selected ? ' survey-ratingOption--selected' : ''}`}
                                  onClick={() => handleAnswerChange(currentQuestion._id!, idx + 1)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleAnswerChange(currentQuestion._id!, idx + 1);
                                    }
                                  }}
                                >
                                  <span className="survey-ratingOptionLabel">{label}</span>
                                  {selected && <span className="survey-ratingOptionTick">✓</span>}
                                </Box>
                              );
                            });
                          })()}
                        </Box>
                      </Box>
                    )}

                    {currentQuestion.questionType === 'multiple_choice' && (
                      <Box className="survey-mc" sx={{ mt: 1 }}>
                        {currentQuestion.options?.map((option: string, optIndex: number) => {
                          const existing = answers.find((a) => a.questionId === currentQuestion._id);
                          const rawVal: any = existing?.answer;
                          const selectedValues: string[] = Array.isArray(rawVal)
                            ? rawVal
                            : rawVal
                            ? [String(rawVal)]
                            : [];
                          const checked = selectedValues.includes(option);

                          const handleToggle = () => {
                            const nextValues = checked
                              ? selectedValues.filter((v) => v !== option)
                              : [...selectedValues, option];
                            handleAnswerChange(currentQuestion._id!, nextValues);
                          };

                          return (
                            <FormControlLabel
                              key={optIndex}
                              className="survey-mcOption"
                              control={<Checkbox checked={checked} onChange={handleToggle} />}
                              label={option}
                            />
                          );
                        })}
                      </Box>
                    )}

                    {currentQuestion.questionType === 'boolean' && (
                      <RadioGroup
                        className="survey-boolean"
                        value={answers.find((a) => a.questionId === currentQuestion._id)?.answer || ''}
                        onChange={(e) => handleAnswerChange(currentQuestion._id!, e.target.value)}
                      >
                        <FormControlLabel className="survey-booleanOption" value="yes" control={<Radio />} label="Yes" />
                        <FormControlLabel className="survey-booleanOption" value="no" control={<Radio />} label="No" />
                      </RadioGroup>
                    )}

                    {shouldAllowComment(currentQuestion) && (
                      <Box className="survey-commentWrap" sx={{ mt: 2 }}>
                        <FormLabel className="survey-commentLabel">
                          <span className="survey-commentLabelInner">
                            <ChatBubbleOutlineIcon className="survey-commentLabelIcon" fontSize="small" />
                            <span>Add a comment (Optional)</span>
                          </span>
                        </FormLabel>
                        <TextField
                          className="survey-comment"
                          fullWidth
                          multiline
                          minRows={2}
                          placeholder="Tell us why you chose this answer (optional)"
                          value={commentDrafts[currentQuestion._id!] ?? ''}
                          onChange={(e) => setCommentDrafts((prev) => ({ ...prev, [currentQuestion._id!]: e.target.value }))}
                          onBlur={() => commitComment(currentQuestion._id!)}
                        />
                      </Box>
                    )}
                  </FormControl>
                </Box>
              ) : null}
            </Box>
        </>
      )}
      </Paper>

      {/* Fixed bottom action bar */}
      <Box className="survey-footer">
        {showIntro ? (
          <>
            <Button
              className="survey-button survey-button--close"
              variant="contained"
              type="button"
              onClick={() => navigate('/dashboard')}
            >
              Close
            </Button>
            <Button
              className="survey-button survey-button--next"
              variant="contained"
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentIdx(0); setShowIntro(false); }}
            >
              Next
            </Button>
          </>
        ) : (
          <>
            {isLast ? (
              <Button
                className="survey-button survey-button--submit survey-button--block"
                type="submit"
                form={formId}
                variant="contained"
                disabled={submitting}
                fullWidth
              >
                {submitting ? <CircularProgress size={24} /> : 'Submit'}
              </Button>
            ) : (
              <Button
                className="survey-button survey-button--next survey-button--block"
                variant="contained"
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); goNext(); }}
                fullWidth
              >
                Next
              </Button>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default TakeSurvey;
